export interface NotificationPermission {
  granted: boolean;
  denied: boolean;
  default: boolean;
}

export interface NotificationOptions {
  title: string;
  body: string;
  icon?: string;
  badge?: string;
  tag?: string;
  requireInteraction?: boolean;
  silent?: boolean;
  data?: any;
  actions?: NotificationAction[];
}

export interface NotificationAction {
  action: string;
  title: string;
  icon?: string;
}

class BrowserNotificationService {
  private permission: NotificationPermission = {
    granted: false,
    denied: false,
    default: false,
  };

  private isSupported(): boolean {
    return "Notification" in window;
  }

  /**
   * Request notification permission from the user
   */
  async requestPermission(): Promise<NotificationPermission> {
    if (!this.isSupported()) {
      console.warn("Browser notifications are not supported");
      return this.permission;
    }

    try {
      const permission = await Notification.requestPermission();

      this.permission = {
        granted: permission === "granted",
        denied: permission === "denied",
        default: permission === "default",
      };

      // Store permission state in localStorage
      localStorage.setItem(
        "notificationPermission",
        JSON.stringify(this.permission)
      );

      return this.permission;
    } catch (error) {
      console.error("Error requesting notification permission:", error);
      return this.permission;
    }
  }

  /**
   * Get current notification permission status
   */
  getPermission(): NotificationPermission {
    if (!this.isSupported()) {
      return this.permission;
    }

    // Check if we have stored permission state
    const stored = localStorage.getItem("notificationPermission");
    if (stored) {
      try {
        this.permission = JSON.parse(stored);
      } catch (error) {
        console.error("Error parsing stored notification permission:", error);
      }
    }

    // Update with current browser permission
    const currentPermission = Notification.permission;
    this.permission = {
      granted: currentPermission === "granted",
      denied: currentPermission === "denied",
      default: currentPermission === "default",
    };

    return this.permission;
  }

  /**
   * Show a system notification
   */
  showNotification(options: NotificationOptions): Notification | null {
    console.log("🔔 showNotification called with options:", options);

    if (!this.isSupported()) {
      console.warn("Browser notifications are not supported");
      return null;
    }

    const permission = this.getPermission();
    console.log("🔔 Current permission:", permission);

    if (!permission.granted) {
      console.warn("Notification permission not granted");
      return null;
    }

    try {
      console.log("🔔 Creating notification with options:", {
        title: options.title,
        body: options.body,
        icon: options.icon || "/favicon.ico",
        tag: options.tag,
      });

      const notification = new Notification(options.title, {
        body: options.body,
        icon: options.icon || "/favicon.ico",
        badge: options.badge || "/favicon.ico",
        tag: options.tag,
        requireInteraction: options.requireInteraction || false,
        silent: options.silent || false,
        data: options.data,
      });

      console.log("🔔 Notification created successfully:", notification);

      // Auto-close notification after 5 seconds unless requireInteraction is true
      if (!options.requireInteraction) {
        setTimeout(() => {
          notification.close();
        }, 5000);
      }

      return notification;
    } catch (error) {
      console.error("Error showing notification:", error);
      return null;
    }
  }

  /**
   * Show notification for new message
   */
  showMessageNotification(
    contactName: string,
    message: string,
    contactId?: string
  ): Notification | null {
    return this.showNotification({
      title: `New message from ${contactName}`,
      body: message,
      tag: `message-${contactId}`,
      icon: "/favicon.ico",
      data: { type: "message", contactId },
    });
  }

  /**
   * Show notification for system events
   */
  showSystemNotification(
    title: string,
    message: string,
    type: string = "system"
  ): Notification | null {
    return this.showNotification({
      title,
      body: message,
      tag: `system-${type}`,
      icon: "/favicon.ico",
      data: { type: "system", systemType: type },
    });
  }

  /**
   * Show notification for user status changes
   */
  showUserStatusNotification(
    userName: string,
    status: "online" | "offline"
  ): Notification | null {
    const emoji = status === "online" ? "🟢" : "🔴";
    return this.showNotification({
      title: `${emoji} ${userName} is ${status}`,
      body: `User ${status} status update`,
      tag: `user-${status}`,
      icon: "/favicon.ico",
      data: { type: "user_status", status },
    });
  }

  /**
   * Show notification for message status updates
   */
  showMessageStatusNotification(
    status: "delivered" | "read" | "failed",
    contactName: string
  ): Notification | null {
    const statusEmoji = {
      delivered: "✅",
      read: "👁️",
      failed: "❌",
    };

    return this.showNotification({
      title: `${statusEmoji[status]} Message ${status}`,
      body: `Message to ${contactName} was ${status}`,
      tag: `message-status-${status}`,
      icon: "/favicon.ico",
      data: { type: "message_status", status },
    });
  }

  /**
   * Close all notifications with a specific tag
   */
  closeNotificationsByTag(tag: string): void {
    // Note: There's no direct way to close notifications by tag in the current API
    // This would need to be implemented by tracking notification instances
    console.log(`Would close notifications with tag: ${tag}`);
  }

  /**
   * Check if notifications are enabled and supported
   */
  isEnabled(): boolean {
    return this.isSupported() && this.getPermission().granted;
  }
}

export const browserNotificationService = new BrowserNotificationService();
export default browserNotificationService;
